import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FieldErrors,
  mergeFieldErrors,
  validationFieldErrorsFrom,
} from "@/shared/lib/errors/validation-error";

/**
 * joinFieldName joins the segments of a field name with a dot. Empty and
 * undefined segments are skipped, e.g. `joinFieldName("", "name")` is
 * `"name"` and `joinFieldName("contact", "name")` is `"contact.name"`.
 */
export function joinFieldName(...parts: (string | undefined)[]): string {
  return parts.filter((p) => p !== undefined && p !== "").join(".");
}

/**
 * matchesFieldName reports whether the error key belongs to the field name,
 * matching whole path segments: "photos" matches "photos" and
 * "photos.1.comment", but not "photosCount". The empty name matches every key.
 */
function matchesFieldName(key: string, name: string): boolean {
  if (name === "") {
    return true;
  }
  return key === name || key.startsWith(`${name}.`);
}

interface ErrValidationFailedContextValue {
  /** The field errors of the scope, keyed by field name. */
  fieldErrors: FieldErrors;
  /**
   * write merges the field errors of a validation error or a document not
   * ready error into the context (see {@link validationFieldErrorsFrom}).
   * Messages of a field already holding errors are appended.
   *
   * @returns true if the error was recognised and written.
   */
  write: (error: unknown) => boolean;
  /** writeFieldErrors merges the field errors into the context. */
  writeFieldErrors: (errors: FieldErrors) => void;
  /** resetField removes the errors of exactly this key. */
  resetField: (key: string) => void;
  /**
   * resetFields removes the errors of the field name and of every key below
   * it (see {@link matchesFieldName}), e.g. of a deleted row.
   */
  resetFields: (name: string) => void;
  /** reset removes all errors. */
  reset: () => void;
}

const noErrors: FieldErrors = {};

const ErrValidationFailedContext =
  createContext<ErrValidationFailedContextValue>({
    fieldErrors: noErrors,
    write: () => false,
    writeFieldErrors: () => undefined,
    resetField: () => undefined,
    resetFields: () => undefined,
    reset: () => undefined,
  });

/**
 * ErrValidationFailedContextProvider holds the field errors of one scope,
 * typically a document. Validation errors (422) of failed mutations and
 * document not ready errors are written into it. Shared input components
 * given a `fieldName` highlight their errors and reset them on input.
 *
 * The backend reports every error at the key of the field in the document
 * (e.g. "visualAssessments.<id>.result", also for a request patching only
 * the assessment), so errors are written as they are; the portal never
 * maps keys.
 *
 * Mount one provider per document. A nested document (e.g. the contract of
 * an offer) and a sheet or dialog editing another entity get a nested
 * provider of their own, so their keys do not collide.
 */
export function ErrValidationFailedContextProvider({
  children,
}: PropsWithChildren) {
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>(noErrors);

  const writeFieldErrors = useCallback((errors: FieldErrors) => {
    setFieldErrors((current) => {
      const merged = mergeFieldErrors(current, errors);
      // Keep the current object if nothing changed, e.g. the same error is
      // written again, so writing does not re-render the consumers.
      return sameFieldErrors(current, merged) ? current : merged;
    });
  }, []);

  const write = useCallback(
    (error: unknown) => {
      const errors = validationFieldErrorsFrom(error);
      if (!errors) {
        return false;
      }
      writeFieldErrors(errors);
      return true;
    },
    [writeFieldErrors],
  );

  const resetField = useCallback((key: string) => {
    setFieldErrors((current) => removeKeys(current, (k) => k === key));
  }, []);

  const resetFields = useCallback((name: string) => {
    setFieldErrors((current) =>
      removeKeys(current, (k) => matchesFieldName(k, name)),
    );
  }, []);

  const reset = useCallback(() => setFieldErrors(noErrors), []);

  const value = useMemo(
    () => ({
      fieldErrors,
      write,
      writeFieldErrors,
      resetField,
      resetFields,
      reset,
    }),
    [fieldErrors, write, writeFieldErrors, resetField, resetFields, reset],
  );

  return (
    <ErrValidationFailedContext.Provider value={value}>
      {children}
    </ErrValidationFailedContext.Provider>
  );
}

function sameFieldErrors(a: FieldErrors, b: FieldErrors): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) {
    return false;
  }
  return keys.every(
    (key) =>
      b[key] !== undefined &&
      a[key].length === b[key].length &&
      a[key].every((message, i) => message === b[key][i]),
  );
}

// removeKeys returns the current errors if no key is removed, so a reset of
// a field without errors does not re-render the consumers of the context.
function removeKeys(
  current: FieldErrors,
  remove: (key: string) => boolean,
): FieldErrors {
  const keys = Object.keys(current);
  if (!keys.some(remove)) {
    return current;
  }
  return Object.fromEntries(
    Object.entries(current).filter(([key]) => !remove(key)),
  );
}

export function useErrValidationFailedContext(): ErrValidationFailedContextValue {
  return useContext(ErrValidationFailedContext);
}

/**
 * useFieldErrors returns the errors of one field: the errors of exactly the
 * key `fieldName` in the nearest {@link ErrValidationFailedContextProvider},
 * merged with the passed `errors`. `reset` removes the field's errors from
 * the context. Call it whenever the user changes the value.
 *
 * Without a `fieldName`, only the passed errors are returned and `reset`
 * does nothing.
 */
export function useFieldErrors(
  fieldName?: string,
  errors?: string[],
): { errors: string[]; invalid: boolean; reset: () => void } {
  const { fieldErrors, resetField } = useErrValidationFailedContext();

  const contextErrors =
    fieldName === undefined ? undefined : fieldErrors[fieldName];
  const merged = useMemo(
    () => [...new Set([...(contextErrors ?? []), ...(errors ?? [])])],
    [contextErrors, errors],
  );

  const reset = useCallback(() => {
    if (fieldName !== undefined) {
      resetField(fieldName);
    }
  }, [fieldName, resetField]);

  return { errors: merged, invalid: merged.length > 0, reset };
}

/**
 * useHasFieldErrors reports whether the nearest
 * {@link ErrValidationFailedContextProvider} holds errors for any of the names,
 * matching whole path segments (see {@link matchesFieldName}). `count` is
 * the number of matching keys. It never resets anything; use it for
 * indicators summarising several fields, e.g. a tab.
 */
export function useHasFieldErrors(names?: string | string[]): {
  hasErrors: boolean;
  count: number;
} {
  const { fieldErrors } = useErrValidationFailedContext();
  const count = countFieldErrors(fieldErrors, names);
  return { hasErrors: count > 0, count };
}

/**
 * countFieldErrors counts the keys of the field errors matching any of the
 * names (see {@link matchesFieldName}). Prefer {@link useHasFieldErrors};
 * use this where a hook cannot be called, e.g. in a loop.
 */
export function countFieldErrors(
  fieldErrors: FieldErrors,
  names?: string | string[],
): number {
  if (names === undefined) {
    return 0;
  }
  const list = typeof names === "string" ? [names] : names;
  return Object.keys(fieldErrors).filter((key) =>
    list.some((name) => matchesFieldName(key, name)),
  ).length;
}

/**
 * useResetFieldsOnChange resets the errors of derived fields — fields without
 * an input of their own, e.g. a price computed from a treatment — whenever
 * their value changes, e.g. after a refetch. `values` maps each field name to
 * its current value, typically read from the RTK Query cache.
 *
 * Call it where it stays mounted while the value can change: usually the
 * component mounting the document's {@link ErrValidationFailedContextProvider},
 * since the value may change on another tab. Never reset derived fields by
 * hand at the places known to change them.
 *
 * A field is reset only if both its previous and its current value are
 * defined and they differ, so the initial load does not reset anything.
 */
export function useResetFieldsOnChange(values: Record<string, unknown>) {
  const { resetField } = useErrValidationFailedContext();
  const previous = useRef<Record<string, string | null>>({});

  // Serialised, so an inline object with the same content does not re-run
  // the effect and objects are compared by content. null stands for a value
  // not loaded yet.
  const dependency = JSON.stringify(
    Object.entries(values).map(([key, value]) => [
      key,
      value === undefined ? null : JSON.stringify(value),
    ]),
  );

  useEffect(() => {
    const current: Record<string, string | null> = Object.fromEntries(
      JSON.parse(dependency) as [string, string | null][],
    );
    for (const [key, value] of Object.entries(current)) {
      const before = previous.current[key] ?? null;
      if (before !== null && value !== null && before !== value) {
        resetField(key);
      }
    }
    previous.current = current;
  }, [dependency, resetField]);
}

/** useResetFieldOnChange is {@link useResetFieldsOnChange} for one field. */
export function useResetFieldOnChange(fieldName: string, value: unknown) {
  useResetFieldsOnChange({ [fieldName]: value });
}

/**
 * useWriteValidationError writes the error into the nearest
 * {@link ErrValidationFailedContextProvider} whenever it changes to a validation
 * or document not ready error. Use it for mutations not run through
 * useDebouncedMutation, which writes by itself.
 *
 * @param error - The error of an RTK Query hook.
 * @param options.onWrite - Called after the error was written, e.g. to close
 *                          a dialog and reset the mutation.
 */
export function useWriteValidationError(
  error: unknown,
  options?: { onWrite?: () => void },
) {
  const { write } = useErrValidationFailedContext();

  // The options are read through a ref, so an inline object or function
  // does not write the same error again on every render.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!write(error)) {
      return;
    }
    optionsRef.current?.onWrite?.();
  }, [error, write]);
}
